// src/pages/SoportesJuridicos.tsx
import React, { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth, getAuthToken } from "../security/AuthLogin";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AmountDropdown from "../components/amountDropdown";
import LoadingLottie from "../components/LoadingLottie";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faLink,
  faLandmark,
  faMapMarkerAlt,
  faUser,
  faFileExcel,
  faSpinner,
  faCheck,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";

import {
  fetchSoportesJuridicos,
  SoporteJuridico,
  FetchSoportesJuridicosParams,
} from "../api/soportesJuridicosApi";

import "../css/concursos.css";
import "../css/checkboxdropdown.css";
import "../css/amountdropdown.css";

interface ISoporteJuridicoConMensajes extends SoporteJuridico {
  leido?: number;
  ultimo_emisor?: string | null;
  ultima_fecha_msg?: string | null;
  estado_soporte_favorito?: string | null;
  fecha_fin_ofertas?: string | null;
  fecha_alta?: string | null;
  dueno_nombre?: string | null;
}

// Mapea estado textual
/** Mapea estado_soporte -> texto visible */
function mapEstado(estado: string | null | undefined): string {
  const e = (estado || "").toLowerCase();
  if (e === "" || e === "pendiente" || e === "en estudio") {
    return "En estudio";
  }
  switch (e) {
    case "valido":
      return "Viable";
    case "rechazado":
      return "No viable";
    case "oculto":
      return "Oculto";
    case "norecomendado":
      return "No recomendado";
    default:
      return estado![0].toUpperCase() + estado!.slice(1);
  }
}

/** Devuelve el slug para la clase CSS `.estado-<slug>` */
function slugEstado(estado: string | null | undefined): string {
  const e = (estado || "").toLowerCase();
  if (e === "" || e === "pendiente" || e === "en estudio") {
    return "en-estudio";
  }
  switch (e) {
    case "valido":
      return "viable";
    case "rechazado":
      return "no-viable";
    case "oculto":
      return "oculto";
    case "norecomendado":
      return "no-recomendado";
    default:
      return e.replace(/ /g, "-");
  }
}

// Prioridad mensajería
function getEmisorPriority(
  s: ISoporteJuridicoConMensajes,
  role: string
): number {
  const em = s.ultimo_emisor;
  if (!em) return 4;
  if (role === "cliente") {
    if (em === "nadie") return 1;
    if (em === "admin_tecnico") return 2;
    return 3;
  } else {
    if (em === "nadie") return 1;
    if (em === "cliente") return 2;
    return 3;
  }
}

// Color de glow externo
function getBackgroundColor(
  s: ISoporteJuridicoConMensajes,
  role: string
): string {
  const est = s.estado_soporte_favorito || s.estado_soporte;
  const em = s.ultimo_emisor || "";
  const ld = Number(s.leido) || 0;
  if (
    (role === "admin" || role === "tecnico") &&
    est?.toLowerCase() === "oculto"
  ) {
    return "black";
  }
  if (role === "cliente") {
    if (em === "cliente") return "rgb(255,0,0)";
    if (em === "admin_tecnico") return ld === 0 ? "rgb(255,255,0)" : "white";
    return "rgb(255,0,0)";
  } else {
    if (em === "nadie" || em === "cliente") return "rgb(255,0,0)";
    if (em === "admin_tecnico") return "rgb(0,255,0)";
    return "white";
  }
}

const SoportesJuridicos: React.FC = () => {
  const { userRole } = useAuth();
  const [soportes, setSoportes] = useState<
    ISoporteJuridicoConMensajes[] | null
  >(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get("page") || "1", 10);
  const limit = parseInt(searchParams.get("limit") || "10", 10);
  const maxPages = Math.ceil(total / limit);

  const [localSearch, setLocalSearch] = useState("");
  const [localFechaInicio, setLocalFechaInicio] = useState("");
  const [localFechaFin, setLocalFechaFin] = useState("");
  const [localImporteDesde, setLocalImporteDesde] = useState("");
  const [localImporteHasta, setLocalImporteHasta] = useState("");
  const [localAutor, setLocalAutor] = useState("");
  const [localAsignado, setLocalAsignado] = useState("");
  const [localNoRespondidos, setLocalNoRespondidos] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);
  const [excelStatus, setExcelStatus] = useState<
    "idle" | "loading" | "success"
  >("idle");

  function cleanNumberString(s: string): string {
    return s.replace(/\./g, "").replace(",", ".");
  }

  const loadSoportes = async () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const ctr = new AbortController();
    abortControllerRef.current = ctr;
    setSoportes(null);
    setLoading(true);
    try {
      const params: FetchSoportesJuridicosParams = { page, limit };
      const cs = searchParams.get("search");
      const fi = searchParams.get("fechaInicio");
      const ff = searchParams.get("fechaFin");
      const di = searchParams.get("importeDesde");
      const hi = searchParams.get("importeHasta");
      const au = searchParams.get("autor");
      const as = searchParams.get("asignado");
      const nr = searchParams.get("noRespondidos");

      if (cs) params.search = cs;
      if (fi) params.fechaInicio = fi;
      if (ff) params.fechaFin = ff;
      if (di) params.importeDesde = cleanNumberString(di);
      if (hi) params.importeHasta = cleanNumberString(hi);
      if ((userRole === "admin" || userRole === "tecnico") && au)
        params.autor = au;
      if ((userRole === "admin" || userRole === "tecnico") && as)
        params.asignado = as;
      if ((userRole === "admin" || userRole === "tecnico") && nr)
        params.noRespondidos = nr;

      const resp = await fetchSoportesJuridicos(params, ctr.signal);
      setSoportes(resp.data);
      setTotal(resp.total);
    } catch (err: any) {
      if (err.name !== "AbortError") {
        console.error(err);
        alert("Error cargando soportes jurídicos");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSoportes();
    return () => {
      abortControllerRef.current?.abort();
    };
    // eslint-disable-next-line
  }, [searchParams]);

  // paginación
  const handlePrev = () => {
    if (page > 1) {
      const np = new URLSearchParams(searchParams);
      np.set("page", String(page - 1));
      setSearchParams(np);
    }
  };
  const handleNext = () => {
    if (page < maxPages) {
      const np = new URLSearchParams(searchParams);
      np.set("page", String(page + 1));
      setSearchParams(np);
    }
  };

  // filtros
  const handleFilter = () => {
    const np = new URLSearchParams();
    if (localSearch) np.set("search", localSearch);
    if (localFechaInicio) np.set("fechaInicio", localFechaInicio);
    if (localFechaFin) np.set("fechaFin", localFechaFin);
    if (localImporteDesde)
      np.set("importeDesde", cleanNumberString(localImporteDesde));
    if (localImporteHasta)
      np.set("importeHasta", cleanNumberString(localImporteHasta));
    if ((userRole === "admin" || userRole === "tecnico") && localAutor) {
      np.set("autor", localAutor);
    }
    if ((userRole === "admin" || userRole === "tecnico") && localAsignado) {
      np.set("asignado", localAsignado);
    }
    if (
      (userRole === "admin" || userRole === "tecnico") &&
      localNoRespondidos
    ) {
      np.set("noRespondidos", "true");
    }
    np.set("page", "1");
    setSearchParams(np);
  };

  const handleReset = () => {
    setLocalSearch("");
    setLocalFechaInicio("");
    setLocalFechaFin("");
    setLocalImporteDesde("");
    setLocalImporteHasta("");
    setLocalAutor("");
    setLocalAsignado("");
    setLocalNoRespondidos(false);
    const np = new URLSearchParams();
    np.set("page", "1");
    setSearchParams(np);
  };

  // ocultar 'oculto' para cliente
  let mostrados: ISoporteJuridicoConMensajes[] = [];
  if (soportes) {
    mostrados =
      userRole === "cliente"
        ? soportes.filter(
            (s) =>
              !s.estado_soporte_favorito ||
              s.estado_soporte_favorito.toLowerCase() !== "oculto"
          )
        : soportes;
  }

  // ordenar
  const sorted = [...mostrados].sort((a, b) => {
    const pA = getEmisorPriority(a, userRole || ""),
      pB = getEmisorPriority(b, userRole || "");
    if (pA !== pB) return pA - pB;
    const dA = a.ultima_fecha_msg ? new Date(a.ultima_fecha_msg).getTime() : 0;
    const dB = b.ultima_fecha_msg ? new Date(b.ultima_fecha_msg).getTime() : 0;
    return dB - dA;
  });

  // Excel
  const handleDownloadExcel = async () => {
    try {
      setExcelStatus("loading");
      const token = getAuthToken();
      if (!token) {
        alert("Inicia sesión");
        setExcelStatus("idle");
        return;
      }
      const apiUrl = `${window.location.protocol}//${window.location.hostname}`;
      const resp = await fetch(`${apiUrl}/api/soportes-juridicos/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(resp.statusText);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "soportes_juridicos.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setExcelStatus("success");
      setTimeout(() => setExcelStatus("idle"), 2000);
    } catch (err) {
      console.error(err);
      alert("Error exportando Excel");
      setExcelStatus("idle");
    }
  };

  const formatearFecha = (fechaStr: string) => {
    if (!fechaStr) return "No hay fecha límite";

    try {
      const fecha = new Date(fechaStr);

      if (isNaN(fecha.getTime())) {
        throw new Error("Fecha inválida");
      }

      const day = fecha.getUTCDate().toString().padStart(2, "0");
      const month = (fecha.getUTCMonth() + 1).toString().padStart(2, "0");
      const year = fecha.getUTCFullYear();
      const hours = fecha.getUTCHours().toString().padStart(2, "0");
      const minutes = fecha.getUTCMinutes().toString().padStart(2, "0");

      return `${day}/${month}/${year} a las ${hours}:${minutes}h`;
    } catch (error) {
      console.error(
        "Error formateando fecha:",
        error,
        "Fecha recibida:",
        fechaStr
      );
      return "Fecha inválida";
    }
  };


  const renderExcel = () => {
    if (excelStatus === "loading") {
      return (
        <>
          <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 5 }} />
          Generando Excel...
        </>
      );
    }
    if (excelStatus === "success") {
      return (
        <>
          <FontAwesomeIcon icon={faCheck} style={{ marginRight: 5 }} />
          ¡Descargado!
        </>
      );
    }
    return (
      <>
        <FontAwesomeIcon icon={faFileExcel} style={{ marginRight: 5 }} />
        Descargar Excel
      </>
    );
  };

  return (
    <div className="container-concursos">
      <h2>Soportes Jurídicos</h2>

      {/* Filtros */}
      <div className="filtros">
        <input
          type="text"
          className="form-control busqueda"
          placeholder="Buscar..."
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleFilter();
            }
          }}
        />
        {(userRole === "admin" || userRole === "tecnico") && (
          <input
            type="text"
            className="form-control busqueda"
            placeholder="Filtrar por cliente..."
            value={localAutor}
            onChange={(e) => setLocalAutor(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleFilter();
              }
            }}
          />
        )}
        {(userRole === "admin" || userRole === "tecnico") && (
          <input
            type="text"
            className="form-control busqueda"
            placeholder="Filtrar por persona asignada..."
            value={localAsignado}
            onChange={(e) => setLocalAsignado(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleFilter();
              }
            }}
          />
        )}
        <AmountDropdown
          desde={localImporteDesde}
          hasta={localImporteHasta}
          onChange={(d, h) => {
            setLocalImporteDesde(d);
            setLocalImporteHasta(h);
          }}
          placeholder="Importe"
        />
        <DatePicker
          selected={localFechaInicio ? new Date(localFechaInicio) : undefined}
          onChange={(dates: [Date | null, Date | null]) => {
            const [s, e] = dates;
            if (s) {
              const d = new Date(s.getTime() - s.getTimezoneOffset() * 60000);
              setLocalFechaInicio(d.toISOString().split("T")[0]);
            } else setLocalFechaInicio("");
            if (e) {
              const d = new Date(e.getTime() - e.getTimezoneOffset() * 60000);
              setLocalFechaFin(d.toISOString().split("T")[0]);
            } else setLocalFechaFin("");
          }}
          startDate={localFechaInicio ? new Date(localFechaInicio) : undefined}
          endDate={localFechaFin ? new Date(localFechaFin) : undefined}
          selectsRange
          isClearable
          dateFormat="dd/MM/yyyy"
          placeholderText="Fecha de alta"
        />
        {(userRole === "admin" || userRole === "tecnico") && (
          <div className="checkbox-filtro">
            <label>
              <input
                type="checkbox"
                checked={localNoRespondidos}
                onChange={(e) => setLocalNoRespondidos(e.target.checked)}
              />
              No respondidos
            </label>
          </div>
        )}
        <button
          onClick={handleFilter}
          className="btn btn-primary"
          disabled={loading}
        >
          Filtrar
        </button>
        <button
          onClick={handleReset}
          className="btn btn-secondary"
          disabled={loading}
        >
          Resetear
        </button>
      </div>
      <div></div>
      {/* Excel */}
      {(userRole === "admin" || userRole === "tecnico") && (
        <div style={{ margin: "1rem 0" }}>
          <button
            onClick={handleDownloadExcel}
            className="btn btn-success"
            disabled={excelStatus === "loading"}
          >
            {renderExcel()}
          </button>
        </div>
      )}

      {/* Listado */}
      <div className="listing-container" style={{ marginTop: "1rem" }}>
        {loading || !soportes ? (
          <LoadingLottie />
        ) : sorted.length === 0 ? (
          <p>No hay soportes jurídicos.</p>
        ) : (
          sorted.map((jur, idx) => {
            // importe
            const tot = jur.importe_total ?? 0;
            const est = jur.importe_estimado ?? 0;
            let txt = "N/A";
            if (tot > 0) {
              txt = new Intl.NumberFormat("es-ES", {
                style: "currency",
                currency: "EUR",
                minimumFractionDigits: tot % 1 === 0 ? 0 : 2,
              }).format(tot);
            } else if (est > 0) {
              txt = new Intl.NumberFormat("es-ES", {
                style: "currency",
                currency: "EUR",
                minimumFractionDigits: est % 1 === 0 ? 0 : 2,
              }).format(est);
            }
            // estado preferente
            const raw = jur.estado_soporte_favorito ?? jur.estado_soporte;
            const vis = mapEstado(raw || "");
            const slug = slugEstado(raw || "");
            const glow = getBackgroundColor(jur, userRole || "");

            return (
              <div
                key={`${jur.id_concurso}-${idx}`}
                className="listing-item-wrapper"
                style={{
                  boxShadow: `0 0 15px 2px ${glow}`,
                  marginBottom: "1rem",
                }}
              >
                <div className="listing-item">
                  <div className="info">
                    <div className="header-concurso">
                      <div className="expediente">{jur.codigo_expediente}</div>
                      <div className="subheader-concurso">
                        <div className={`estado estado-${slug}`}>{vis}</div>
                        {jur.fecha_publicacion && (
                          <div className="publication-date">
                            {new Date(
                              jur.fecha_publicacion
                            ).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="title">{jur.titulo}</div>
                    <div className="details">
                      <div>
                        <FontAwesomeIcon icon={faLandmark} className="icon" />{" "}
                        {jur.entidad_compradora}
                      </div>
                      <div>
                        <FontAwesomeIcon
                          icon={faMapMarkerAlt}
                          className="icon"
                        />{" "}
                        {jur.pais}
                        {jur.localidad ? ` - ${jur.localidad}` : ""}
                      </div>
                    </div>

                    <div className="extra-info">
                      <div className="fin-plazo">
                        {jur.fecha_fin_ofertas ? (
                          <>
                            <span>Hasta&nbsp;</span>
                            <span>{formatearFecha(jur.fecha_fin_ofertas)}</span>
                          </>
                        ) : (
                          <span>Fecha límite no disponible</span>
                        )}
                      </div>
                    </div>

                    {(userRole === "admin" || userRole === "tecnico") &&
                      jur.cliente_nombre && (
                        <div
                          style={{
                            marginTop: "0.8rem",
                            fontSize: "0.95rem",
                            color: "#444",
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faUser}
                            style={{ marginRight: "0.4rem" }}
                          />
                          <strong>Cliente:</strong>{" "}
                          <em>{jur.cliente_nombre}</em>
                        </div>
                      )}
                  </div>

                  {jur.fecha_alta && (
                    <div
                      style={{
                        marginBottom: ".8rem",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faCalendarAlt}
                        style={{ marginRight: ".4rem" }}
                      />
                      <strong>Fecha alta:</strong>{" "}
                      <em>
                        {new Date(jur.fecha_alta).toLocaleDateString("es-ES", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })}{" "}
                        a las{" "}
                        {new Date(jur.fecha_alta).toLocaleTimeString("es-ES", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </em>
                    </div>
                  )}

                  <div className="footer-concurso">
                    <div className="importe">{txt}</div>
                    <div className="actions-concurso">
                      {(userRole === "admin" || userRole === "tecnico") && (
                        <div
                          style={{
                            marginRight: "0.5rem",
                            padding: "0.25rem 0.75rem",
                            borderRadius: "12px",
                            backgroundColor: "rgba(0, 123, 255, 0.15)", // color azul suave y semitransparente
                            fontSize: "0.875rem",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Asignado a {jur.dueno_nombre ?? "nadie"}
                        </div>
                      )}
                      <Link
                        to={`/soporte-juridico/${jur.id_concurso}/${jur.id_usuario}`}
                      >
                        <FontAwesomeIcon icon={faEye} title="Ver detalle" />
                      </Link>
                      {jur.enlace && (
                        <a
                          href={jur.enlace}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ marginLeft: "0.5rem" }}
                        >
                          <FontAwesomeIcon
                            icon={faLink}
                            title="Enlace oficial"
                          />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Paginación */}
      <div className="pagination-concursos" style={{ marginTop: "1rem" }}>
        <button onClick={handlePrev} disabled={page <= 1}>
          Anterior
        </button>
        <span>
          Página {page} de {maxPages}
        </span>
        <button onClick={handleNext} disabled={page >= maxPages}>
          Siguiente
        </button>
      </div>
    </div>
  );
};

export default SoportesJuridicos;
